import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '@/integrations/supabase/client';
import { MapPin, Car, User } from 'lucide-react';
import { calculateDistance } from '@/utils/distanceCalculation';

const RideTrackingPage = () => {
  const { rideId } = useParams<{ rideId: string }>();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const driverMarkerRef = useRef<L.Marker | null>(null);
  const routePolylineRef = useRef<L.Polyline | null>(null);
  const pickupMarkerRef = useRef<L.Marker | null>(null);
  const dropoffMarkerRef = useRef<L.Marker | null>(null);
  const fitDoneRef = useRef(false);
  const lastRouteOriginRef = useRef<{ lat: number; lng: number } | null>(null);

  const [ride, setRide] = useState<any>(null);
  const [driverProfile, setDriverProfile] = useState<any>(null);
  const [eta, setEta] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!rideId) { setError('Invalid tracking link'); return; }

    const fetchRide = async () => {
      const { data, error: err } = await supabase
        .from('ride_requests')
        .select('*')
        .eq('id', rideId)
        .maybeSingle();

      if (err || !data) { setError('Ride not found'); return; }
      setRide(data);

      if (data.driver_id) {
        const [{ data: driverLoc }, { data: profile }] = await Promise.all([
          supabase.from('ride_driver_profiles').select('current_latitude, current_longitude').eq('user_id', data.driver_id).maybeSingle(),
          supabase.from('profiles').select('full_name, avatar_url').eq('id', data.driver_id).maybeSingle(),
        ]);
        setDriverProfile({ ...driverLoc, ...profile });
      }
    };

    fetchRide();
  }, [rideId]);

  useEffect(() => {
    if (!ride || !mapRef.current || mapInstanceRef.current) return;

    const center = { lat: ride.pickup_latitude, lng: ride.pickup_longitude };
    const map = new L.Map(mapRef.current, { center: [center.lat, center.lng], zoom: 14, zoomControl: false });

    new L.TileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OSM © CARTO', subdomains: 'abcd', maxZoom: 20,
    }).addTo(map);

    mapInstanceRef.current = map;

    const createPin = (color: string) => L.divIcon({
      html: `<div style="display:flex;flex-direction:column;align-items:center;"><div style="width:14px;height:14px;background:${color};border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div><div style="width:2px;height:16px;background:${color};border-radius:0 0 2px 2px;margin-top:-1px;"></div></div>`,
      className: '', iconSize: [20, 32], iconAnchor: [10, 32],
    });

    pickupMarkerRef.current = L.marker([ride.pickup_latitude, ride.pickup_longitude], { icon: createPin('#3b82f6') }).addTo(map);
    dropoffMarkerRef.current = L.marker([ride.dropoff_latitude, ride.dropoff_longitude], { icon: createPin('#f97316') }).addTo(map);

    map.fitBounds([
      [ride.pickup_latitude, ride.pickup_longitude],
      [ride.dropoff_latitude, ride.dropoff_longitude],
    ], { padding: [60, 60] });

    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [ride]);

  // Poll driver location
  useEffect(() => {
    if (!ride?.driver_id || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    const poll = async () => {
      const { data: loc } = await supabase
        .from('ride_driver_profiles')
        .select('current_latitude, current_longitude')
        .eq('user_id', ride.driver_id)
        .maybeSingle();

      if (!loc?.current_latitude || !loc?.current_longitude) return;

      const driverLat = Number(loc.current_latitude);
      const driverLng = Number(loc.current_longitude);

      const icon = L.divIcon({
        html: `<div style="width:18px;height:18px;border-radius:999px;background:#1a1a2e;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.3);"></div>`,
        iconSize: [24, 24], iconAnchor: [12, 12], className: '',
      });

      if (driverMarkerRef.current) {
        driverMarkerRef.current.setLatLng([driverLat, driverLng]);
      } else {
        driverMarkerRef.current = L.marker([driverLat, driverLng], { icon }).addTo(map);
      }

      // Check ride status
      const { data: rideUpdate } = await supabase
        .from('ride_requests')
        .select('status')
        .eq('id', ride.id)
        .maybeSingle();

      if (rideUpdate) setRide((prev: any) => ({ ...prev, status: rideUpdate.status }));

      // Route
      const target = ride.status === 'in_progress'
        ? { lat: ride.dropoff_latitude, lng: ride.dropoff_longitude }
        : { lat: ride.pickup_latitude, lng: ride.pickup_longitude };

      const lastO = lastRouteOriginRef.current;
      const moved = lastO ? calculateDistance(lastO.lat, lastO.lng, driverLat, driverLng) : Infinity;

      if (!routePolylineRef.current || moved > 0.12) {
        try {
          const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${driverLng},${driverLat};${target.lng},${target.lat}?overview=full&geometries=geojson`);
          const data = await res.json();
          const route = data?.routes?.[0];
          if (route?.duration) setEta(Math.max(1, Math.round(route.duration / 60)));
          const coords = route?.geometry?.coordinates;
          if (coords?.length > 1) {
            const latlngs: L.LatLngExpression[] = coords.map(([lng, lat]: number[]) => [lat, lng]);
            if (routePolylineRef.current) routePolylineRef.current.setLatLngs(latlngs);
            else routePolylineRef.current = L.polyline(latlngs, { color: '#0F766E', weight: 5, opacity: 0.85 }).addTo(map);
            if (!fitDoneRef.current) { fitDoneRef.current = true; map.fitBounds(L.latLngBounds(latlngs), { padding: [60, 80] }); }
          }
          lastRouteOriginRef.current = { lat: driverLat, lng: driverLng };
        } catch { /* keep existing */ }
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [ride?.driver_id, ride?.status]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <Car className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h1 className="text-xl font-bold text-foreground mb-1">Ride not found</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!ride) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const isCompleted = ride.status === 'completed' || ride.status === 'cancelled';

  return (
    <div className="h-screen w-full relative bg-background">
      <div ref={mapRef} className="absolute inset-0" />

      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="bg-card rounded-t-3xl shadow-2xl border-t border-border">
          <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mt-3 mb-2" />
          <div className="px-5 pb-6">
            {isCompleted ? (
              <div className="text-center py-4">
                <h2 className="text-lg font-bold text-foreground mb-1">
                  {ride.status === 'completed' ? 'Ride completed' : 'Ride cancelled'}
                </h2>
                <p className="text-sm text-muted-foreground">This ride has ended.</p>
              </div>
            ) : (
              <>
                <p className="text-lg font-bold text-foreground mb-3">
                  {ride.status === 'in_progress' ? `Arriving in ${eta ?? '—'} min` : `Pickup in ${eta ?? '—'} min`}
                </p>

                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-muted overflow-hidden flex items-center justify-center">
                    {driverProfile?.avatar_url ? (
                      <img src={driverProfile.avatar_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <User className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{driverProfile?.full_name || 'Driver'}</p>
                    <p className="text-xs text-muted-foreground">Live tracking</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-muted/50 rounded-xl p-3">
                  <div className="mt-1 flex flex-col items-center">
                    <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                    <span className="my-1 h-6 w-0.5 bg-border" />
                    <span className="h-2.5 w-2.5 rounded-full bg-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground">Pickup</p>
                    <p className="text-sm font-semibold text-foreground truncate">{ride.pickup_address?.split(',')[0]}</p>
                    <p className="mt-2 text-xs text-muted-foreground">Destination</p>
                    <p className="text-sm font-semibold text-foreground truncate">{ride.dropoff_address?.split(',')[0]}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RideTrackingPage;
